import { useState, useEffect, useCallback, useMemo } from 'react';
import { TextField, Typography, Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { BasicDialog } from '../../../components';
import { orderSelectors, useSelector, useDispatch, addResource } from '../../../state';

interface LabelDocument {
  id: string;
  documentId: number;
  documentFeeType: string;
  documentFeeTypeDisplay: string;
  quantity: number;
}

interface DocQuantityConfirmModalProps {
  open: boolean;
  handleClose: () => void;
  onConfirm: () => void;
  confirmBtnText?: string;
}

export function DocQuantityConfirmModal({
  open,
  handleClose,
  onConfirm,
  confirmBtnText = 'Confirm & Continue',
}: DocQuantityConfirmModalProps) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const selectedOrder = useSelector(orderSelectors.selectedOrder);

  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);

  const labelDocuments: LabelDocument[] = useMemo(() => {
    if (!selectedOrder?.documents) return [];
    return selectedOrder.documents
      .filter(
        (d) =>
          d.id &&
          d.document?.id &&
          (d.documentFeeType === 'CartonLabelDocumentFee' ||
            d.documentFeeType === 'ItemLabelDocumentFee'),
      )
      .map((d) => ({
        id: d.id as string,
        documentId: d.document!.id as number,
        documentFeeType: d.documentFeeType || '',
        documentFeeTypeDisplay: d.documentFeeTypeDisplay || '',
        quantity: d.quantity || 1,
      }));
  }, [selectedOrder?.documents]);

  useEffect(() => {
    const initial: Record<string, number> = {};
    labelDocuments.forEach((doc) => {
      initial[doc.id] = doc.quantity || 1;
    });
    setQuantities(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleQuantityChange = useCallback(
    (docId: string, value: string) => {
      const num = parseInt(value, 10);
      if (!isNaN(num) && num >= 1) {
        setQuantities((prev) => ({ ...prev, [docId]: num }));
      }
    },
    [],
  );

  const handleConfirm = useCallback(async () => {
    if (!selectedOrder?.id) return;
    setSubmitting(true);

    const hasChanges = labelDocuments.some(
      (doc) => quantities[doc.id] !== doc.quantity,
    );

    if (hasChanges) {
      await Promise.all(
        labelDocuments
          .filter((doc) => quantities[doc.id] !== doc.quantity)
          .map((doc) =>
            dispatch(
              addResource({
                baseUrl: `/orders/${selectedOrder.id}/documents`,
                payload: {
                  orderID: selectedOrder.id,
                  documentFeeType: doc.documentFeeType,
                  quantity: quantities[doc.id],
                  billableToCustomer: true,
                  documents: [{ id: doc.documentId }],
                },
              }),
            ),
          ),
      );
    }

    setSubmitting(false);
    onConfirm();
  }, [dispatch, labelDocuments, onConfirm, quantities, selectedOrder?.id]);

  return (
    <BasicDialog
      open={open}
      handleClose={handleClose}
      title='Confirm Document Quantity'
      callback={handleConfirm}
      btnText={confirmBtnText}
      disableCallback={submitting}
      submitting={submitting}
      paperProps={{ className: classes.paper }}
      maxWidth='sm'
    >
      <Typography className={classes.subtitle}>
        Please verify and enter the number of document pages for billing.
      </Typography>
      <Box className={classes.documentsContainer}>
        {labelDocuments.map((doc) => (
          <Box key={doc.id} className={classes.documentRow}>
            <Typography className={classes.documentType}>
              {doc.documentFeeTypeDisplay || doc.documentFeeType}
            </Typography>
            <TextField
              type='number'
              label='Number of pages'
              value={quantities[doc.id] || 1}
              onChange={(e) => handleQuantityChange(doc.id, e.target.value)}
              size='small'
              className={classes.quantityInput}
              slotProps={{ htmlInput: { min: 1 } }}
              variant='outlined'
            />
          </Box>
        ))}
      </Box>
    </BasicDialog>
  );
}

const useStyles = makeStyles()((theme) => ({
  paper: {
    width: 500,
    padding: theme.spacing(1),
  },
  subtitle: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  documentsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  documentRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
  },
  documentType: {
    fontWeight: 600,
    fontSize: 14,
  },
  quantityInput: {
    width: 140,
  },
}));
